import { DataReport, LogData, LogExtraData } from "@/utils/data_report"
import { assert } from "@/utils/tools"
import { OnCursorChange } from "./cursor_event_processor"
import {
  OnTransferProgressListener,
  TransferFileListener,
} from "./file_transfer_task"
import { _KCDeskSignaling } from "./signaling"
import { MediaStreams, _KCDeskWebRTC } from "./webrtc"
import { isAssistLogin } from "@/pages/login/login_hook"

// 与远端连接的状态
export const CONNECT_STATE_PEER_NOT_FOUND = -1
export const CONNECT_STATE_NETWORK_ERROR = -2
export const CONNECT_STATE_NETWORK_RESTORE = -3
export const CONNECT_STATE_SERVICE_INSTALL_FAIL = -4
export const CONNECT_STATE_REQ_PEER_PWD = 0
export const CONNECT_STATE_PEER_REQ_CONN = 1
export const CONNECT_STATE_PEER_ACCEPT_CONN = 2
export const CONNECT_STATE_PEER_REJECT_CONN = 3
export const CONNECT_STATE_PWD_VERIFY_FAIL = 4
export const CONNECT_STATE_PEER_OFFLINE = 5
export const CONNECT_STATE_LINK_FULL = 6
export const CONNECT_STATE_PEER_CLOSE = 7
export const CONNECT_STATE_PEER_CANCEL = 8
export const CONNECT_STATE_CONN_CONNECTED = 9
export const CONNECT_STATE_CONN_DISCONNECTED = 10
export const CONNECT_STATE_CONN_CONNECTING = 11
export const CONNECT_STATE_CONN_FAILED = 12
export const CONNECT_STATE_CONN_BUSY = 13
export const CONNECT_STATE_PEER_NODISPLAY = 14 // agent 获取桌面失败
export const CONNECT_STATE_DRM_RENDER_START = 15 // 第一帧渲染成功
export const CONNECT_STATE_MAX_RETRY = 16 // 达到最大重连次数
export const CONNECT_STATE_RECONN = 17 // 重连
export const CONNECT_STATE_SIGNALING_EXPIRED = 18 // 信令过期

// 连接云桌面的参数
export interface PeerConnConfig {
  token: string
  forceRelay: boolean
  preferIp: string | null // 优先使用同源的ip(gateWayIp)
  // Relay 服务器 (云桌面设备所在的内网网关) 公网ip
  relaySever: RTCIceServer | null
  // Relay 服务器内网ip
  remotePeerInnerIp: string | null
}

// 本地的 device id
let localDevId: string | null = null

export interface ConnUserInfo {
  // 账号名
  userName: string
  nickName: string
  // 云电脑 deviceId
  deviceId: string
  // 云电脑 desktopId
  desktopId: string
}

export interface ServerConfig {
  addr: string
  user: string
  pwd: string
}

// 视频流统计信息
export interface ConnStateInfo {
  inputVideoData: {
    fps: number
    curBytesReceived: number
    lostRate: number
    curFrameRecv: number
    curFramesDecoded: number
    codec: string | null // 编码协议
    fecPacketsReceived: number
    fecBytesReceived: number
    frameWidth: number // 视频宽
    frameHeight: number // 视频高
    decoder: string // 解码器名字；仅在 inbound-rtp.video.decoderImplementation 存在时为真实值，否则为 "unknown"
    freezeCount: number // 卡顿次数
    totalFreezesDuration: number // 卡顿累加总时长, 秒
    framesReceived: number
    framesDecoded: number
    packetsReceived: number
    framesDropped: number
    keyFramesDecoded: number
  }

  otherData: {
    rtt: number // 网络往返延迟
    transportCurBytesReceived: number // 计算码率（transport中的）
    isPlayingAudio: boolean
  }
  // output_video上报事件中的字段
  outputVideoData: {
    mid: string
    frameWidth: number
    frameHeight: number
    fps: number
    bytesSent: number
    encoder: string | null
    totalEncodeTime: number
    framesEncoded: number
    lostRate: number
    jitter: number
  }
}

// 传给 SDK 的回调
export interface SDKCallback {
  // 与远端连接状态变化
  onConnect: (state: number, errCode?: number) => void
  // 获取远端设备支持的分辨率列表
  onGetDisplayInfo: (resolutions: Resolution[]) => void
  // 获取 Agent 主显示器当前分辨率
  onGetAgentResolution: (resolution: Resolution) => void
  // 获取Agent版本
  onGetAgentVersion: (num: number) => void
  // 获取键盘方案版本
  onGetKeyboardVersion: (keyboardVersion?: string) => void
  // 获取是否为游戏类型云电脑
  onGetIsGameCloudComputer: (isGameCloudComputer: boolean) => void
  // Agent 通知客户端开启/关闭麦克风
  onMicStateChange: (isOpen: boolean) => void
  // Agent 通知客户端开启/关闭摄像头
  onCameraStateChange: (isOpen: boolean) => void
  // Agent 通知客户端：云电脑禁止安装安全/虚拟化软件
  onBlockAppInstallation?: () => void
  // 收到管理后台发起的远程协助请求（assist status: started）
  // [srcDevId] 发起请求的管理后台 deviceId，用于回复 confirmed/denied
  onAssistRequest?: (srcDevId: string | null) => void
  // 管理后台中断了远程协助（assist status: aborted）
  onAssistAborted?: () => void
  // 售后成功连接到云电脑（assist status: connected）
  onAssistConnected?: () => void
}

// 鼠标事件
export interface MouseEventData {
  event: MouseEventType
  x: number
  y: number
  buttonState: number
  keyboardState: number
}

export enum MouseEventType {
  move = 2,
  up = 3,
  down = 4,
  wheel = 6,
}

// 键盘事件
export interface KeyboardEventData {
  event: KeyboardEventType
  code: number
  state: number
}

export enum KeyboardEventType {
  keydown = 0,
  keyup = 1,
  repeat = 3,
}

// 屏幕分辨率
export interface Resolution {
  width: number
  height: number
}

export interface ScaleRatioConfig {
  screenIndex: number
  current: number
  recommended: number
  scaleRatios: number[]
}

// 粘贴板数据类型
export interface CBData {
  type: string
  data: Uint8Array
}
// 远端设备粘贴板复制时的回调
export type OnRemoteCopy = (data: CBData) => void

// 信令服务器
let signaling: _KCDeskSignaling | null = null
// 当前的 WebRTC 连接
let webrtc: _KCDeskWebRTC | null = null

let callback: SDKCallback | null = null

// 远程协助回调（独立于 callback，生命周期跟随信令连接常驻，不随 WebRTC 释放清空）。
// 对齐 android setOnAssistMessage 独立于 setOnWebRTCMessage 的设计。
let assistCallback: Pick<
  SDKCallback,
  "onAssistRequest" | "onAssistAborted" | "onAssistConnected"
> | null = null

let connUserInfo: ConnUserInfo | null = null

function getLogData(data?: any): LogExtraData {
  let logData: LogExtraData = {}
  if (connUserInfo !== null) {
    const { desktopId, deviceId, userName, nickName } = connUserInfo
    logData = {
      localDevId,
      desktopId,
      deviceId,
      user: userName,
      nickName,
    }
  }
  if (webrtc !== null) {
    logData = { ...logData, conn_id: webrtc.connId }
  }
  if (data) {
    logData = { ...logData, ...data }
  }
  return logData
}

// 上报日志
export function _reportSDKLog(event: string, data?: any) {
  DataReport.reportLog(new LogData(event, getLogData(data)))
}

export function _reportSDKError(errCode: number, data?: any) {
  DataReport.reportError(errCode, getLogData(data))
}

export function _getLocalDevId(): string | null {
  return localDevId
}

function setSDKCallback(cb: SDKCallback | null): void {
  callback = cb
}

// 设置远程协助回调（常驻，对齐 android KCSdkCallback 的 assist 回调）
function setAssistCallback(
  cb: Pick<SDKCallback, "onAssistRequest" | "onAssistAborted" | "onAssistConnected">
): void {
  assistCallback = cb
}

// 远程协助消息分发（对齐 android KCSdk._onAssistMessage）
function onAssistMessage(
  status: string,
  _data: any,
  src: string | null
): void {
  switch (status) {
    case "started":
      // 管理后台发起远程协助请求
      assistCallback?.onAssistRequest?.(src)
      break
    case "aborted":
      // 管理后台中断了远程协助
      assistCallback?.onAssistAborted?.()
      break
    case "connected":
      // 售后成功连接到云电脑
      assistCallback?.onAssistConnected?.()
      break
    case "confirmed":
    case "denied":
      // 客户端自身发出的回复状态，收到忽略
      break
  }
}

// 连接信令服务器
// [remoteDevId] 目标云电脑 deviceId，用于 agent_room_id header（确保同 room）
function connectToSignaling(
  devId: string,
  token: string,
  remoteDevId: string
): void {
  // 新登录必须使用最新 token；回收上一次可能仍处于重连中的 socket。
  if (signaling !== null) {
    signaling.close()
  }
  signaling = new _KCDeskSignaling()

  localDevId = devId
  // 协助方登录模式（角色B）：让远端识别协助身份
  signaling.setAssistMode(isAssistLogin)
  // 注册远程协助消息监听（独立于 WebRTC，生命周期随信令连接常驻）
  signaling.setOnAssistMessage(onAssistMessage)
  signaling.connect(localDevId, token, remoteDevId)
}

// 信令服务器是否已连接
function isSignalingConnected() {
  return signaling?.isConnected
}

function closeSignalingConnection(): void {
  signaling?.close()
  signaling = null
}

// 等待信令在指定时间内连接成功
async function waitSignalingConnected(timeoutMs = 3000): Promise<boolean> {
  assert(
    signaling !== null,
    "Cannot wait signaling connected, signaling is null"
  )
  return await signaling.waitUntilConnected(timeoutMs)
}

/**
 *  连接到远端用户
 * @param config WebRTC 连接配置
 * @param userInfo 发起连接的用户信息
 * @returns 连接 id
 */
function connectToPeer(config: PeerConnConfig, userInfo: ConnUserInfo): string {
  connUserInfo = userInfo
  assert(
    signaling !== null,
    "Cannot set remote peer callback, signaling is null"
  )

  assert(webrtc === null, "There are existing webrtc connection")
  const peer = new _KCDeskWebRTC({
    config,
    callbacks: {
      sendMsgBySignaling,
      waitSignalingConnected,
      // WebRTC 连接状态变化
      onConnect: (state: number, errCode?: number) => {
        callback?.onConnect(state, errCode)
      },
      onGetDisplayInfo: (resulutions: Resolution[]) => {
        callback?.onGetDisplayInfo(resulutions)
      },
      onGetAgentResolution: (resolution: Resolution) => {
        callback?.onGetAgentResolution(resolution)
      },
      onGetAgentVersion: (num: number) => {
        callback?.onGetAgentVersion(num)
      },
      onGetKeyboardVersion: (keyboardVersion?: string) => {
        callback?.onGetKeyboardVersion(keyboardVersion)
      },
      onGetIsGameCloudComputer: (isGameCloudComputer: boolean) => {
        callback?.onGetIsGameCloudComputer(isGameCloudComputer)
      },
      onMicStateChange: (isOpen: boolean) => {
        callback?.onMicStateChange(isOpen)
      },
      onCameraStateChange: (isOpen: boolean) => {
        callback?.onCameraStateChange(isOpen)
      },
      onBlockAppInstallation: () => {
        callback?.onBlockAppInstallation?.()
      },
    },
  })

  signaling.setRemotePeerCallback({
    // WebRTC Peer 连接状态变化
    onPeerConnect(user: string, state: number) {
      // 上一次手动断开时遗留的信令事件不能影响新建的 PeerConnection。
      if (webrtc === peer) callback?.onConnect(state)
    },

    // 接收到远端的 answer
    onAnswer: function (answer: RTCSessionDescriptionInit): void {
      if (webrtc === peer) void peer.getAnswer(answer)
    },

    // 接收到远端的 ICE candidate
    onIceCandidate: function (data: RTCIceCandidateInit): void {
      if (webrtc === peer) void peer.onRemoteICECandidate(data)
    },

    onSignalingExpired: function (): void {
      if (webrtc === peer) callback?.onConnect(CONNECT_STATE_SIGNALING_EXPIRED)
    },
  })

  // 设置回调后才公开当前实例，防止旧信令回调路由到这次的新连接。
  webrtc = peer
  signaling.send("status", connUserInfo.deviceId)
  // 上报开始连接云桌面
  _reportSDKLog("connect_rtc")
  // 开始连接
  peer.start()
  return peer.connId
}

// 关闭与远端的连接
function closePeerConnection(): void {
  if (webrtc !== null) {
    connUserInfo = null
    webrtc.disconnect()
    webrtc = null
  }
  // 不再关闭信令连接：保留信令常驻用于接收远程协助请求（对齐 android exitCloudPC
  // 只销毁 WebRTC、保留 _signaling）。信令在登出/切用户/关应用时由
  // closeSignalingConnection 显式关闭。
}

// 回复远程协助请求（对齐 android KCSdk.sendAssistReply）
function sendAssistReply(targetDevId: string, accepted: boolean): void {
  signaling?.sendAssistReply(targetDevId, accepted)
}

// 探测信令连接健康度（对齐 android detectHeartbeat）
async function detectHeartbeat(
  count = 10,
  intervalMs = 1000
): Promise<[number, number]> {
  if (signaling === null) {
    return [0, 0]
  }
  return await signaling.detectHeartbeat(count, intervalMs)
}

// 设置远端视频流分辨率
function setVideoResolution({
  index = 0,
  width,
  height,
}: {
  index?: number
  width: number
  height: number
}): void {
  webrtc?.setVideoResolution(width, height, index)
}

async function getScaleRatioConfig(
  index = 0
): Promise<ScaleRatioConfig | null> {
  if (webrtc === null) return null
  const config = await webrtc.getScaleRatioConfig(index)
  if (config === null) return null
  return {
    screenIndex: Number(config.index),
    current: Number(config.currentDpi),
    recommended: Number(config.recommendedDpiIndex),
    scaleRatios: (config.dpiList ?? []).map(Number),
  }
}

async function setScaleRatio(scaleRatio: number, index = 0): Promise<boolean> {
  if (webrtc === null) return false
  return await webrtc.setScaleRatio(scaleRatio, index)
}

// 发送鼠标事件
function sendMouseData(event: MouseEventData): void {
  webrtc?.sendMouseData(event)
}

// 发送键盘事件
function sendKeyboardData(event: KeyboardEventData): void {
  webrtc?.sendKeyboardData(event)
}

// 发送清理键盘事件
function sendCleanKeyboardData(): void {
  webrtc?.sendCleanKeyboardData()
}

// 透传客户端动态配置给远端 agent
function sendClientConfig(config: Record<string, unknown>): void {
  webrtc?.sendClientConfig(config)
}

// 发送心跳包
function sendHeartbeatData(heartBeatText: string): void {
  webrtc?.sendHeartbeatData(heartBeatText)
}

// 获取连接统计信息
async function getConnStats(): Promise<ConnStateInfo | null> {
  if (webrtc === null) {
    return null
  }
  return await webrtc.getStatsData()
}

// 发送本地文件到远端
// @Param file 本地文件
// @Return 文件传输任务 ID
function sendLocalFile(file: File) {
  webrtc?.fileTransferManager.beginSendFile(file)
}

/**
 * 通过信令服务器发送 message 类型的消息给远端
 * @param data 消息内容
 */
function sendMsgBySignaling(data: any): void {
  // 委托给 signaling.sendMessage，复用其 assistMode 的 src 逻辑与地址封装
  // （对齐 android sendMessage 信封：room/device_id/src/master/version/data）
  signaling?.sendMessage(data)
}

// 设置鼠标样式改变回调
function setOnCursorChange(callback: OnCursorChange) {
  webrtc?.cursorProcessor.setOnCursorChange(callback)
}

// 设置远端粘贴板数据
function setRemoteClipboardData(type: string, data: ArrayBuffer) {
  webrtc?.setClipboardData(type, data)
}

// 设置远端桌面壁纸
async function setWallpaper(url: string): Promise<number> {
  if (webrtc === null) {
    return -1
  }
  return await webrtc.setWallpaper(url)
}

function setOnRemoteCopy(onCopy: OnRemoteCopy) {
  webrtc?.setOnRemoteCopy(onCopy)
}

// 从远端选择文件传输到本地
function selectRecvFile() {
  webrtc?.fileTransferManager.selectRecvFile()
}

// 设置接收文件的回调
function setRecvFileListener(listener: TransferFileListener) {
  webrtc?.fileTransferManager.setRecvFileListener(listener)
}

// 设置文件接收进度回调
function setFileRecvProgressListener(
  taskId: number,
  listener: OnTransferProgressListener
) {
  webrtc?.fileTransferManager.setFileRecvProgressListener(taskId, listener)
}

// 设置发送文件回调
function setSendFileListener(listener: TransferFileListener) {
  webrtc?.fileTransferManager.setSendFileListener(listener)
}

// 设置文件发送进度回调
function setFileSendProgressListener(
  taskId: number,
  listener: OnTransferProgressListener
) {
  webrtc?.fileTransferManager.setFileSendProgressListener(taskId, listener)
}

/**
 * 停止传输文件
 * @param taskId 传输任务id
 */
function stopTransferFile(taskId: number) {
  webrtc?.fileTransferManager.stopTask(taskId)
}

// 鼠标样式图片
export interface MouseCursor {
  // 鼠标图片像素数据
  data: ImageData
  // 鼠标图片宽度
  width: number
  // 鼠标图片高度
  height: number
  // x 轴偏移量
  offsetX: number
  // y 轴偏移量
  offsetY: number
}

// 开启音频输入
function enableAudioInput(stream: MediaStream): Promise<void> {
  return webrtc?.enableAudioInput(stream) ?? Promise.resolve()
}

// 关闭音频输入
function disableAudioInput(): Promise<void> {
  return webrtc?.disableAudioInput() ?? Promise.resolve()
}

// 开启视频输入
function enableVideoInput(stream: MediaStream): Promise<void> {
  return webrtc?.enableVideoInput(stream) ?? Promise.resolve()
}

// 关闭视频输入
function disableVideoInput(): Promise<void> {
  return webrtc?.disableVideoInput() ?? Promise.resolve()
}

// 上报第一帧
function reportFirstFrameRender() {
  webrtc?.reportFirstFrameRender()
}

function getMediaStreams(): MediaStreams | null {
  if (webrtc === null) {
    return null
  }
  return webrtc.getMediaStreams()
}

// 将 WebRTC 统计信息写入 csv 文件
function writeStatsInfoToCSVFile() {
  webrtc?.connStatistic.prepareWriteToCSVFile()
}

export const KCSDK = {
  setSDKCallback,
  setAssistCallback,
  connectToSignaling,
  isSignalingConnected,
  closeSignalingConnection,
  waitSignalingConnected,
  connectToPeer,
  closePeerConnection,
  sendAssistReply,
  detectHeartbeat,
  sendLocalFile,
  setVideoResolution,
  getScaleRatioConfig,
  setScaleRatio,
  sendMouseData,
  sendKeyboardData,
  sendCleanKeyboardData,
  sendClientConfig,
  sendHeartbeatData,
  getConnStats,
  setOnCursorChange,
  setRemoteClipboardData,
  setWallpaper,
  setOnRemoteCopy,
  selectRecvFile,
  setRecvFileListener,
  setFileRecvProgressListener,
  setSendFileListener,
  setFileSendProgressListener,
  stopTransferFile,
  enableAudioInput,
  disableAudioInput,
  enableVideoInput,
  disableVideoInput,
  reportFirstFrameRender,
  getMediaStreams,
  writeStatsInfoToCSVFile,
  sendMsgBySignaling,
}
